import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { MainLayout } from './components/layout/MainLayout.js';
import { AuthLayout } from './components/layout/AuthLayout.js';
import { ProtectedRoute, RoleRoute } from './components/common/ProtectedRoute.js';
import { CORE_WINDOWABLE_PAGES } from './plugin/coreWindowablePages.js';
import { QAOverlay } from './components/preview/QAOverlay.js';
import { buildPluginRoutes } from './plugin/PluginRoutesHost.js';
import { buildExtensionRoutes } from './plugin/ExtensionRoutesHost.js';
import { useModules } from './hooks/useModules.js';
import { useAuthStore } from './stores/authStore.js';
import CopilotProvider from './components/copilot/CopilotProvider.js';
import { PlatformWebHostProvider } from '@elmes/plugin-sdk/web';
import { CORE_WEB_HOST } from './plugin/platformWebHost.js';
// EAGER (gh#2607 PR-3): a redirect rendered through Suspense would flash a spinner on
// its way to a route it does not even paint. The module is a dozen lines.
import { DmLegacyRedirect } from './modules/pm/DmLegacyRedirect.js';
import { DM_ACTIVITY_ROUTE } from './modules/pm/dmActivityUrl.js';

// LoginPage stays eager — it's the cold-boot entry point for unauthenticated
// users; rendering through Suspense would add a spinner flash on load.
import { LoginPage } from './pages/auth/LoginPage.js';

// Roadmap #97 — every other route is split off the index bundle. Auth helper
// pages, user/group/notes/files/memory CRUDs all lazy-load on first nav.
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage.js').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage.js').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage.js').then((m) => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.js').then((m) => ({ default: m.VerifyEmailPage })));
const OAuthCallbackPage = lazy(() => import('./pages/auth/OAuthCallbackPage.js').then((m) => ({ default: m.OAuthCallbackPage })));
const OAuthConsentPage = lazy(() => import('./pages/auth/OAuthConsentPage.js').then((m) => ({ default: m.OAuthConsentPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));
const UsersListPage = lazy(() => import('./pages/users/UsersListPage.js').then((m) => ({ default: m.UsersListPage })));
const UserFormPage = lazy(() => import('./pages/users/UserFormPage.js').then((m) => ({ default: m.UserFormPage })));
const GroupsListPage = lazy(() => import('./pages/groups/GroupsListPage.js').then((m) => ({ default: m.GroupsListPage })));
const GroupFormPage = lazy(() => import('./pages/groups/GroupFormPage.js').then((m) => ({ default: m.GroupFormPage })));
const FilesPage = lazy(() => import('./pages/files/FilesPage.js').then((m) => ({ default: m.FilesPage })));
const MemoryListPage = lazy(() => import('./pages/memory/MemoryListPage.js').then((m) => ({ default: m.MemoryListPage })));
const MemoryFormPage = lazy(() => import('./pages/memory/MemoryFormPage.js').then((m) => ({ default: m.MemoryFormPage })));
const UnifiedMemoryPage = lazy(() => import('./pages/memory/UnifiedMemoryPage.js').then((m) => ({ default: m.UnifiedMemoryPage })));

const AIPage = lazy(() => import('./pages/ai/AIPage.js'));
const AISettingsPage = lazy(() => import('./pages/ai/AISettingsPage.js'));
const AIHubLayout = lazy(() => import('./pages/ai/AIHubLayout.js'));
const AIHubDashboard = lazy(() => import('./pages/ai/AIHubDashboard.js'));
const AIProfileList = lazy(() => import('./pages/ai/AIProfileList.js'));
const AIProfileEditor = lazy(() => import('./pages/ai/AIProfileEditor.js'));
const AIMemoryPage = lazy(() => import('./pages/ai/AIMemoryPage.js'));
const MemoryForgetPage = lazy(() => import('./pages/ai/MemoryForgetPage.js'));
const AITasksPage = lazy(() => import('./pages/ai/AITasksPage.js'));
const AIAuditPage = lazy(() => import('./pages/ai/AIAuditPage.js'));
const AIIssuesPage = lazy(() => import('./pages/ai/AIIssuesPage.js'));
const AIIssueDetailPage = lazy(() => import('./pages/ai/AIIssueDetailPage.js'));
const AIToolsPage = lazy(() => import('./pages/ai/AIToolsPage.js'));
const AgentStudioPage = lazy(() => import('./pages/ai/AgentStudioPage.js'));
const AgentLiveExecutionView = lazy(() => import('./pages/ai/AgentLiveExecutionView.js'));
const AgentPermissionsView = lazy(() => import('./pages/ai/AgentPermissionsView.js'));
const AgentArtifactsPage = lazy(() => import('./pages/ai/AgentArtifactsPage.js'));
const PipelinesPage = lazy(() => import('./pages/pipeline/PipelinesPage.js'));
const PipelineEditorPage = lazy(() => import('./pages/pipeline/PipelineEditorPage.js'));
const PipelineTemplatesPage = lazy(() => import('./pages/pipeline/PipelineTemplatesPage.js'));
const AINotificationsPage = lazy(() => import('./pages/ai/AINotificationsPage.js'));
const CanvasPage = lazy(() => import('./pages/canvas/CanvasPage.js'));
const CanvasEditorPage = lazy(() => import('./pages/canvas/CanvasEditorPage.js'));
const CanvasPublicViewPage = lazy(() => import('./pages/canvas/CanvasPublicViewPage.js'));
const UISystemPage = lazy(() => import('./pages/uiSystem/UISystemPage.js'));
const SystemPage = lazy(() => import('./pages/system/SystemPage.js'));
const FileEditorPage = lazy(() => import('./pages/files/FileEditorPage.js'));
const ClaudeCliSessionListPage = lazy(() => import('./pages/claudeCli/SessionListPage.js'));
const ClaudeCliSessionViewPage = lazy(() => import('./pages/claudeCli/SessionViewPage.js'));
const ClaudeCliSpawnPage = lazy(() => import('./pages/claudeCli/SpawnPage.js'));
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout.js'));
const SecurityPage = lazy(() => import('./pages/settings/SecurityPage.js'));
const ConnectionsPage = lazy(() => import('./pages/settings/ConnectionsPage.js'));
const EvalProfilePage = lazy(() => import('./pages/ai/eval/EvalProfilePage.js'));
const FederationEvalPage = lazy(() => import('./pages/ai/eval/FederationEvalPage.js'));
const PreviewAdminPage = lazy(() => import('./pages/preview/PreviewAdminPage.js'));

// Atlas pages
const AtlasOverviewPage = lazy(() => import('./pages/ai/atlas/AtlasOverviewPage.js'));
const AtlasGraphPage = lazy(() => import('./pages/ai/atlas/AtlasGraphPage.js'));
const AtlasModulesPage = lazy(() => import('./pages/ai/atlas/AtlasModulesPage.js'));
const AtlasNodeDetailPage = lazy(() => import('./pages/ai/atlas/AtlasNodeDetailPage.js'));
const AtlasNodeSlugResolver = lazy(() => import('./pages/ai/atlas/AtlasNodeSlugResolver.js'));
const AtlasDataModelPage = lazy(() => import('./pages/ai/atlas/AtlasDataModelPage.js'));
const AtlasToolsPage = lazy(() => import('./pages/ai/atlas/AtlasToolsPage.js'));
const AtlasNodesAdminPage = lazy(() => import('./pages/ai/atlas/AtlasNodesAdminPage.js'));
const AtlasDiffPage = lazy(() => import('./pages/ai/atlas/AtlasDiffPage.js'));
const AtlasTopologyPage = lazy(() => import('./pages/ai/atlas/AtlasTopologyPage.js'));

// Roadmap pages
const RoadmapDashboardPage = lazy(() => import('./pages/ai/roadmap/RoadmapDashboardPage.js'));
const RoadmapInboxPage = lazy(() => import('./pages/ai/roadmap/RoadmapInboxPage.js'));
const RoadmapKanbanPage = lazy(() => import('./pages/ai/roadmap/RoadmapKanbanPage.js'));
const RoadmapMatrixPage = lazy(() => import('./pages/ai/roadmap/RoadmapMatrixPage.js'));
const RoadmapTechTreePage = lazy(() => import('./pages/ai/roadmap/RoadmapTechTreePage.js'));
const RoadmapItemDetailPage = lazy(() => import('./pages/ai/roadmap/RoadmapItemDetailPage.js'));
const RoadmapTimelinePage = lazy(() => import('./pages/ai/roadmap/RoadmapTimelinePage.js'));
const SprintPlanningPage = lazy(() => import('./pages/ai/roadmap/SprintPlanningPage.js'));

// Observability pages
const TraceViewerPage = lazy(() => import('./pages/ai/TraceViewerPage.js'));
const TriggersPage = lazy(() => import('./pages/ai/TriggersPage.js'));
const CostDashboardPage = lazy(() => import('./pages/ai/CostDashboardPage.js'));

// Activity feed page
const FeedPage = lazy(() => import('./pages/ai/feed/FeedPage.js'));

// Releases page
const ReleasesPage = lazy(() => import('./pages/ai/releases/ReleasesPage.js'));

// Profile page — gh#649
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage.js').then((m) => ({ default: m.ProfilePage })));

// PM core module — Entity OS task views (roadmap #452, epic #447)
const PmPage = lazy(() => import('./modules/pm/PmPage.js'));
// `DmSprintDashboard` is gone entirely (gh#2607 PR-5): its full-page route retired into
// the board's tree mode in PR-3, its launcher window and component in PR-5. Sprint
// membership is the board's tree section; the corpus's freshness is the Overview tab's
// `Sprint sync` block.
const DmActivityTab = lazy(() => import('./modules/pm/DmActivityTab.js'));
const DmProjectView = lazy(() => import('./modules/pm/DmProjectView.js'));
const DmMemorySearch = lazy(() => import('./modules/pm/DmMemorySearch.js'));
// DM shell (gh#2607 PR-3) — `/dm` tab bar + the four surfaces authored for it.
const DmShellLayout = lazy(() => import('./modules/pm/DmShellLayout.js'));
const DmOverviewTab = lazy(() => import('./modules/pm/DmOverviewTab.js'));
const DmInboxPage = lazy(() => import('./modules/pm/DmInboxPage.js'));
const DmIssuesTab = lazy(() => import('./modules/pm/DmIssuesTab.js'));
const DmRegistryTab = lazy(() => import('./modules/pm/DmRegistryTab.js'));
const DmPoliciesTab = lazy(() => import('./modules/pm/DmPoliciesTab.js'));
const DmDocsPage = lazy(() => import('./modules/pm/DmDocsPage.js'));
const DmAgentsTab = lazy(() => import('./modules/pm/DmAgentsTab.js'));
const DmSettingsTab = lazy(() => import('./modules/pm/DmSettingsTab.js'));

const ConsolePage = lazy(() => import('./modules/console/ConsolePage.js'));
// Same source as the launcher/Sidebar gate; fail-closed to admin if the entry is ever missing.
const CONSOLE_REQUIRED_ROLE =
  CORE_WINDOWABLE_PAGES.find((p) => p.pageRoute === '/console')?.requiredRole ?? 'admin';

// Email Admin pages are now dynamically mounted from manifest.pages via pageRegistry (#183)

function SuspenseFallback() {
  return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" /></div>;
}

export default function App() {
  // Faz 6 (#183): fetch plugin pages for dynamic route mounting.
  // filterByRole=false so all active plugin routes are registered (ProtectedRoute handles auth).
  // enabled gated on auth — unauthenticated /login visitors must not trigger
  // /system/modules → 401 → hard-reload loop.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { modules, loading } = useModules({ filterByRole: false, enabled: isAuthenticated });

  return (
    // The host extensions consume via usePlatformWebHost(); mounted in every build, outside auth.
    <PlatformWebHostProvider host={CORE_WEB_HOST}>
    {/* Outer Suspense covers every lazy route. Inner per-route Suspense
        wrappers (kept for backward-compat) just shadow this one — no harm. */}
    <Suspense fallback={<SuspenseFallback />}>
    <CopilotProvider>
    <QAOverlay />
    <Routes>
      {/* Public canvas share route (no auth) */}
      <Route path="/canvas/public/:token" element={<Suspense fallback={<SuspenseFallback />}><CanvasPublicViewPage /></Suspense>} />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Remote MCP OAuth consent (roadmap #420) — standalone focused page, no
            MainLayout chrome. Under ProtectedRoute so login is enforced first. */}
        <Route
          path="/oauth/consent"
          element={<Suspense fallback={<SuspenseFallback />}><OAuthConsentPage /></Suspense>}
        />
        <Route element={<MainLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/users" element={<UsersListPage />} />
          <Route path="/users/new" element={<UserFormPage />} />
          <Route path="/users/:id/edit" element={<UserFormPage />} />
          <Route path="/groups" element={<GroupsListPage />} />
          <Route path="/groups/new" element={<GroupFormPage />} />
          <Route path="/groups/:id/edit" element={<GroupFormPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/files/:id/edit" element={<Suspense fallback={<SuspenseFallback />}><FileEditorPage /></Suspense>} />
          <Route path="/canvas" element={<Suspense fallback={<SuspenseFallback />}><CanvasPage /></Suspense>} />
          <Route path="/canvas/:id" element={<Suspense fallback={<SuspenseFallback />}><CanvasEditorPage /></Suspense>} />
          <Route path="/design-system" element={<Suspense fallback={<SuspenseFallback />}><UISystemPage /></Suspense>} />
          <Route path="/ui-system" element={<Navigate to="/design-system" replace />} />
          {/* PM core module — Entity OS task list/board/detail (roadmap #452, epic #447) */}
          <Route path="/pm" element={<Suspense fallback={<SuspenseFallback />}><PmPage /></Suspense>} />
          {/* ── THE DM SHELL (gh#2607 PR-3) ────────────────────────────────────
              One `/dm` parent with a tab bar (DmShellLayout) over the addressable
              children `DM_SHELL_TABS` names — counted nowhere here, because a number in
              this comment is a second recording of that array's length
              ([[reference_prose_number_discipline_hub]]).
              Each child keeps its own direct-URL route, so a deep link or a
              refresh lands on the tab rather than falling through the catch-all `*`
              into /dashboard (gh#1418) — the reason every dm page had a top-level route
              before, unchanged by the nesting.

              THE INBOX IS A TAB AS OF PR-5, and that reverses PR-4's decision rather
              than drifting from it: PR-4 made it a rail BADGE and declined a tab on the
              argument that the rail was where it belonged. PR-5 retired the `dm-inbox`
              launcher window, and the rail deliberately does not resolve questions — so
              without a tab the platform's only `pm.question.answer` surface would have
              had no address at all. */}
          <Route path="/dm" element={<Suspense fallback={<SuspenseFallback />}><DmShellLayout /></Suspense>}>
            <Route index element={<Navigate to="/dm/overview" replace />} />
            <Route path="overview" element={<Suspense fallback={<SuspenseFallback />}><DmOverviewTab /></Suspense>} />
            {/* The board IS `DmProjectView`: the `?project/?mode/?panes/?inspect`
                contract (`dmProjectViewUrl`) is UNCHANGED and lives on inside the
                shell — the page still reads its four modes off the address. */}
            <Route path="board" element={<Suspense fallback={<SuspenseFallback />}><DmProjectView /></Suspense>} />
            <Route path="inbox" element={<Suspense fallback={<SuspenseFallback />}><DmInboxPage /></Suspense>} />
            {/* W8-5a — one inbox item over the same list (`q180` jumps here). */}
            <Route path="inbox/:id" element={<Suspense fallback={<SuspenseFallback />}><DmInboxPage /></Suspense>} />
            {/* THE TRIAGE QUEUE (W-IV B). Read-only: creating a finding is an agent door
                and lowering a tier is an operator act performed in chat — see the tab. */}
            <Route path="issues" element={<Suspense fallback={<SuspenseFallback />}><DmIssuesTab /></Suspense>} />
            {/* THE ACTIVITY LEDGER (DM W2 A1) — the tab formerly at `runs`. `/dm/runs`
                stays registered one line down as a FORWARD rather than being deleted:
                it is the address the shell itself published for two phases, so it is in
                bookmarks and in saved compositor layouts. */}
            <Route path="activity" element={<Suspense fallback={<SuspenseFallback />}><DmActivityTab /></Suspense>} />
            <Route path="runs" element={<DmLegacyRedirect pathname={DM_ACTIVITY_ROUTE} />} />
            {/* The memory page did not move — only its route did. Its query is still NOT
                in the address by design; see the page header on why the door is a POST. */}
            <Route path="memory" element={<Suspense fallback={<SuspenseFallback />}><DmMemorySearch /></Suspense>} />
            <Route path="policies" element={<Suspense fallback={<SuspenseFallback />}><DmPoliciesTab /></Suspense>} />
            {/* W3-B D3 — a page, not a tab: the shell's help drawer links here. */}
            <Route path="docs" element={<Suspense fallback={<SuspenseFallback />}><DmDocsPage /></Suspense>} />
            <Route path="registry" element={<Suspense fallback={<SuspenseFallback />}><DmRegistryTab /></Suspense>} />
            <Route path="agents" element={<Suspense fallback={<SuspenseFallback />}><DmAgentsTab /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<SuspenseFallback />}><DmSettingsTab /></Suspense>} />
          </Route>
          {/* ── PRE-SHELL ADDRESSES ────────────────────────────────────────────
              Kept as ROUTES rather than deleted: they are in bookmarks, in the
              launcher's `pageRoute` entries and in links this platform itself wrote.
              `DmLegacyRedirect` carries the query string across — see that module on
              why a bare <Navigate> would silently empty a shared workspace link. */}
          <Route path="/dm-project" element={<DmLegacyRedirect pathname="/dm/board" />} />
          <Route path="/dm-inbox" element={<DmLegacyRedirect pathname="/dm/inbox" />} />
          {/* Sprint membership groups are a reading of the tree — the URL contract's
              DEFAULT mode, so the forward writes no key (dmShellRoutes.test.ts pins it). */}
          <Route path="/dm-sprints" element={<DmLegacyRedirect pathname="/dm/board" />} />
          <Route path="/dm-runs" element={<DmLegacyRedirect pathname={DM_ACTIVITY_ROUTE} />} />
          <Route path="/dm-memory" element={<DmLegacyRedirect pathname="/dm/memory" />} />
          {/* Entity OS Console A1 — admin operator console; direct-URL route so /console
              deep-links don't fall through the catch-all `*` into /dashboard (gh#1418,
              roadmap #474). Admin-only visibility is enforced by requiredRole in
              coreWindowablePages + the console API's route-level fresh-admin gate; RoleRoute
              redirects a non-admin direct-URL hit before the shell mounts (gh#1565). */}
          <Route element={<RoleRoute requiredRole={CONSOLE_REQUIRED_ROLE} />}>
            <Route path="/console" element={<Suspense fallback={<SuspenseFallback />}><ConsolePage /></Suspense>} />
          </Route>
          {/* Bare /atlas/:slug — global search atlas hits (atlas + graphrag adapters) link to
              `/atlas/<slug>`. Without this direct-URL route the single-segment path falls through
              the catch-all `*` into /dashboard (gh#1639). AtlasNodeSlugResolver looks the slug up
              and redirects to the canonical /ai/atlas/nodes/:id detail (or an inline not-found —
              never a silent dashboard redirect). */}
          <Route path="/atlas/:slug" element={<Suspense fallback={<SuspenseFallback />}><AtlasNodeSlugResolver /></Suspense>} />
          <Route path="/memory" element={<MemoryListPage />} />
          <Route path="/memory/new" element={<MemoryFormPage />} />
          <Route path="/memory/:key/edit" element={<MemoryFormPage />} />
          {/* Roadmap #413 — Birleşik Memory Browser (federated, read-only) */}
          <Route path="/memory/browser" element={<Suspense fallback={<SuspenseFallback />}><UnifiedMemoryPage /></Suspense>} />
          <Route path="/system" element={<Suspense fallback={<SuspenseFallback />}><SystemPage /></Suspense>} />
          {/* Settings hub — SettingsLayout provides nav + <Outlet>. A bare /settings
              route is required so the catch-all `*` no longer swallows it into /dashboard
              (fixes PR#1318 nav gap). */}
          <Route path="/settings" element={<Suspense fallback={<SuspenseFallback />}><SettingsLayout /></Suspense>}>
            <Route index element={<Navigate to="/settings/security" replace />} />
            <Route path="security" element={<Suspense fallback={<SuspenseFallback />}><SecurityPage /></Suspense>} />
            <Route path="connections" element={<Suspense fallback={<SuspenseFallback />}><ConnectionsPage /></Suspense>} />
          </Route>
          <Route path="/profile" element={<Suspense fallback={<SuspenseFallback />}><ProfilePage /></Suspense>} />
          <Route path="/preview" element={<Suspense fallback={<SuspenseFallback />}><PreviewAdminPage /></Suspense>} />
          {/* Roadmap #412 — federation eval trend (exact path before profile/:id) */}
          <Route path="/ai/eval" element={<Suspense fallback={<SuspenseFallback />}><FederationEvalPage /></Suspense>} />
          <Route path="/ai/eval/profile/:id" element={<Suspense fallback={<SuspenseFallback />}><EvalProfilePage /></Suspense>} />
          {/* AI Hub */}
          <Route path="/ai" element={<Suspense fallback={<SuspenseFallback />}><AIHubLayout /></Suspense>}>
            <Route index element={<Suspense fallback={<SuspenseFallback />}><AIHubDashboard /></Suspense>} />
            <Route path="profiles" element={<Suspense fallback={<SuspenseFallback />}><AIProfileList /></Suspense>} />
            <Route path="profiles/new" element={<Suspense fallback={<SuspenseFallback />}><AIProfileEditor /></Suspense>} />
            <Route path="profiles/:id" element={<Suspense fallback={<SuspenseFallback />}><AIProfileEditor /></Suspense>} />
            <Route path="chat" element={<Suspense fallback={<SuspenseFallback />}><AIPage /></Suspense>} />
            <Route path="chat/:id" element={<Suspense fallback={<SuspenseFallback />}><AIPage /></Suspense>} />
            <Route path="tools" element={<Suspense fallback={<SuspenseFallback />}><AIToolsPage /></Suspense>} />
            <Route path="agents" element={<Suspense fallback={<SuspenseFallback />}><AgentStudioPage /></Suspense>} />
            <Route path="agents/:id" element={<Suspense fallback={<SuspenseFallback />}><AgentStudioPage /></Suspense>} />
            {/* F12 Sprint B — Permission drill-down (#311) */}
            <Route path="agents/:id/permissions" element={<Suspense fallback={<SuspenseFallback />}><AgentPermissionsView /></Suspense>} />
            {/* F12 Sprint D — Standalone artifacts view (#315) */}
            <Route path="agents/:id/artifacts" element={<Suspense fallback={<SuspenseFallback />}><AgentArtifactsPage /></Suspense>} />
            {/* F12 Sprint A — Live execution view (#308) */}
            <Route path="pipelines/runs/:pipelineId/:runId/live" element={<Suspense fallback={<SuspenseFallback />}><AgentLiveExecutionView /></Suspense>} />
            <Route path="issues" element={<Suspense fallback={<SuspenseFallback />}><AIIssuesPage /></Suspense>} />
            <Route path="issues/new" element={<Suspense fallback={<SuspenseFallback />}><AIIssueDetailPage /></Suspense>} />
            <Route path="issues/:id" element={<Suspense fallback={<SuspenseFallback />}><AIIssueDetailPage /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<SuspenseFallback />}><AINotificationsPage /></Suspense>} />
            <Route path="memory" element={<Suspense fallback={<SuspenseFallback />}><AIMemoryPage /></Suspense>} />
            <Route path="memory/forget" element={<Suspense fallback={<SuspenseFallback />}><MemoryForgetPage /></Suspense>} />
            <Route path="tasks" element={<Suspense fallback={<SuspenseFallback />}><AITasksPage /></Suspense>} />
            <Route path="audit" element={<Suspense fallback={<SuspenseFallback />}><AIAuditPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<SuspenseFallback />}><AISettingsPage /></Suspense>} />
            <Route path="pipelines" element={<Suspense fallback={<SuspenseFallback />}><PipelinesPage /></Suspense>} />
            <Route path="pipelines/new" element={<Suspense fallback={<SuspenseFallback />}><PipelineEditorPage /></Suspense>} />
            <Route path="pipelines/templates" element={<Suspense fallback={<SuspenseFallback />}><PipelineTemplatesPage /></Suspense>} />
            <Route path="pipelines/:id" element={<Suspense fallback={<SuspenseFallback />}><PipelineEditorPage /></Suspense>} />
            {/* Atlas routes */}
            <Route path="atlas" element={<Suspense fallback={<SuspenseFallback />}><AtlasOverviewPage /></Suspense>} />
            <Route path="atlas/graph" element={<Suspense fallback={<SuspenseFallback />}><AtlasGraphPage /></Suspense>} />
            <Route path="atlas/modules" element={<Suspense fallback={<SuspenseFallback />}><AtlasModulesPage /></Suspense>} />
            <Route path="atlas/data-model" element={<Suspense fallback={<SuspenseFallback />}><AtlasDataModelPage /></Suspense>} />
            <Route path="atlas/tools" element={<Suspense fallback={<SuspenseFallback />}><AtlasToolsPage /></Suspense>} />
            <Route path="atlas/admin" element={<Suspense fallback={<SuspenseFallback />}><AtlasNodesAdminPage /></Suspense>} />
            <Route path="atlas/diff" element={<Suspense fallback={<SuspenseFallback />}><AtlasDiffPage /></Suspense>} />
            <Route path="atlas/topology" element={<Suspense fallback={<SuspenseFallback />}><AtlasTopologyPage /></Suspense>} />
            <Route path="atlas/nodes/:id" element={<Suspense fallback={<SuspenseFallback />}><AtlasNodeDetailPage /></Suspense>} />
            {/* Roadmap routes */}
            <Route path="roadmap" element={<Suspense fallback={<SuspenseFallback />}><RoadmapDashboardPage /></Suspense>} />
            <Route path="roadmap/inbox" element={<Suspense fallback={<SuspenseFallback />}><RoadmapInboxPage /></Suspense>} />
            <Route path="roadmap/kanban" element={<Suspense fallback={<SuspenseFallback />}><RoadmapKanbanPage /></Suspense>} />
            <Route path="roadmap/matrix" element={<Suspense fallback={<SuspenseFallback />}><RoadmapMatrixPage /></Suspense>} />
            <Route path="roadmap/tech-tree" element={<Suspense fallback={<SuspenseFallback />}><RoadmapTechTreePage /></Suspense>} />
            <Route path="roadmap/timeline" element={<Suspense fallback={<SuspenseFallback />}><RoadmapTimelinePage /></Suspense>} />
            <Route path="roadmap/sprint-planning" element={<Suspense fallback={<SuspenseFallback />}><SprintPlanningPage /></Suspense>} />
            <Route path="roadmap/items/:id" element={<Suspense fallback={<SuspenseFallback />}><RoadmapItemDetailPage /></Suspense>} />
            {/* Activity feed */}
            <Route path="feed" element={<Suspense fallback={<SuspenseFallback />}><FeedPage /></Suspense>} />
            {/* Releases */}
            <Route path="releases" element={<Suspense fallback={<SuspenseFallback />}><ReleasesPage /></Suspense>} />
            {/* F14 Sprint B — Trace Viewer (#340 #341) */}
            <Route path="observability/traces" element={<Suspense fallback={<SuspenseFallback />}><TraceViewerPage /></Suspense>} />
            {/* F14 Sprint C — Cost Dashboard (#342 #343) */}
            <Route path="observability/cost" element={<Suspense fallback={<SuspenseFallback />}><CostDashboardPage /></Suspense>} />
            {/* F15 Sprint C — Triggers (#355) */}
            <Route path="triggers" element={<Suspense fallback={<SuspenseFallback />}><TriggersPage /></Suspense>} />
          </Route>
          {/* Faz 6 (#183): Dynamic plugin routes from manifest.pages.
              Placed BEFORE the wildcard * redirect to ensure correct route matching.
              PluginRoutesHost handles layout detection (nested groups for EmailAdminLayout). */}
          {buildPluginRoutes(modules)}
          <Route path="/claude-cli" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSessionListPage /></Suspense>} />
          <Route path="/claude-cli/spawn" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSpawnPage /></Suspense>} />
          <Route path="/claude-cli/sessions/:uuid" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSessionViewPage /></Suspense>} />
          {/* Build-time web slot's direct routes — LAST, so an equal-rank tie goes to core
              (React Router keeps definition order); coreRoutes.ts refuses a path core matches. */}
          {buildExtensionRoutes(<SuspenseFallback />)}
        </Route>
      </Route>

      {/* Default redirect — guarded by plugin loading state to prevent race condition
          where async plugin routes (e.g. /chat) haven't mounted yet and wildcard
          fires first, redirecting the user to /dashboard (#fix-plugin-route-loading-guard) */}
      <Route
        path="*"
        element={loading
          ? <div className="animate-pulse p-4 text-gray-400">Yükleniyor...</div>
          : <Navigate to="/dashboard" replace />}
      />
    </Routes>
    </CopilotProvider>
    </Suspense>
    </PlatformWebHostProvider>
  );
}
